import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import TopNavigation from '../../components/ui/TopNavigation';
import KPICard from './components/KPICard';
import ScanTrendsChart from './components/ScanTrendsChart';
import TopPerformingQRs from './components/TopPerformingQRs';
import RealTimeScanFeed from './components/RealTimeScanFeed';
import GlobalControls from './components/GlobalControls';
import GeographicMiniHeatmap from './components/GeographicMiniHeatmap';
import DetailedAnalyticsPanel from './components/DetailedAnalyticsPanel';
import { QRAnalyticsService } from '../../services/qrAnalyticsService';
import SystemVersionManager from '../../components/ui/SystemVersionManager';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';



const MainAnalyticsDashboard = () => {
  const { user, userProfile } = useAuth();
  const [dateRange, setDateRange] = useState({ 
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 
    end: new Date() 
  });
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [loading, setLoading] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [trackingSetupStatus, setTrackingSetupStatus] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    totalScans: 0,
    totalQRCodes: 0,
    totalCampaigns: 0,
    styledQRCodes: 0,
    scanTrends: [],
    topPerformingQRs: [],
    recentScans: [],
    geographicData: []
  });
  const [showDetailedAnalytics, setShowDetailedAnalytics] = useState(false);

  const refreshDashboardData = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);

      const trackingSetup = await QRAnalyticsService?.ensureAllQRCodesHaveTracking();
      setTrackingSetupStatus(trackingSetup);

      const [
        kpiData,
        trends,
        topQRs,
        recentScans,
        geoData
      ] = await Promise.all([
        QRAnalyticsService?.getKPIData(dateRange, selectedCampaign !== 'all' ? selectedCampaign : null),
        QRAnalyticsService?.getScanTrends(dateRange, selectedCampaign !== 'all' ? selectedCampaign : null),
        QRAnalyticsService?.getTopPerformingQRs(dateRange, selectedCampaign !== 'all' ? selectedCampaign : null, 5),
        QRAnalyticsService?.getRecentScans(10),
        QRAnalyticsService?.getGeographicData(dateRange, selectedCampaign !== 'all' ? selectedCampaign : null)
      ]);

      setDashboardData({
        totalScans: kpiData?.totalScans || 0,
        totalQRCodes: kpiData?.totalQRCodes || 0,
        totalCampaigns: kpiData?.totalCampaigns || 0,
        styledQRCodes: kpiData?.styledQRCodes || 0,
        scanTrends: trends || [],
        topPerformingQRs: topQRs || [],
        recentScans: recentScans || [],
        geographicData: geoData || []
      });
      setLastUpdateTime(new Date());
    } catch (error) {
      console.log('Dashboard data fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [user, dateRange, selectedCampaign]);

  // Redirect to auth if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      // Navigate to auth if not authenticated
    }
  }, [user, loading]);

  // Record page access activity
  useEffect(() => {
    if (user) {
      // Record activity
    }
  }, [user]);

  // Initial data load
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) return;
      
      setLoading(true);
      await refreshDashboardData();
      setLoading(false);
    };

    fetchDashboardData();
  }, [user, refreshDashboardData]);

  // Real-time subscription for new QR scans
  useEffect(() => {
    if (!user) return;

    let channel;
    
    try {
      channel = supabase?.channel('dashboard_qr_scans')?.on(
          'postgres_changes',
          { 
            event: 'INSERT', 
            schema: 'public', 
            table: 'qr_scans',
            filter: `qr_code_id=in.(${dashboardData?.topPerformingQRs?.map(qr => qr?.id)?.join(',') || 'none'})`
          },
          (payload) => {
            console.log('New QR scan detected:', payload?.new);
            // Refresh dashboard data when new scan is detected
            refreshDashboardData();
          }
        )?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public', 
            table: 'analytics_daily'
          },
          (payload) => {
            console.log('Analytics updated:', payload?.new);
            // Refresh dashboard data when analytics are updated
            refreshDashboardData();
          }
        )?.subscribe();

      console.log('Real-time subscription active for dashboard updates');
    } catch (error) {
      console.log('Real-time subscription error:', error);
    }

    return () => {
      if (channel) {
        supabase?.removeChannel(channel);
        console.log('Real-time subscription cleaned up');
      }
    };
  }, [user, refreshDashboardData, dashboardData?.topPerformingQRs]);

  // Fallback polling mechanism (every 20 seconds) as backup
  useEffect(() => {
    if (!user) return;

    const intervalId = setInterval(() => {
      const now = new Date();
      const timeSinceLastUpdate = now - lastUpdateTime;
      
      // Only refresh if it's been more than 20 seconds since last update
      if (timeSinceLastUpdate >= 20000) {
        console.log('Background refresh triggered (20s interval)');
        refreshDashboardData();
      }
    }, 20000);

    return () => {
      clearInterval(intervalId);
    };
  }, [user, lastUpdateTime, refreshDashboardData]);

  // Page visibility API - pause updates when page is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        // Refresh data when page becomes visible again
        const timeSinceLastUpdate = new Date() - lastUpdateTime;
        if (timeSinceLastUpdate >= 30000) { // 30 seconds threshold
          console.log('Page became visible - refreshing data');
          refreshDashboardData();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, lastUpdateTime, refreshDashboardData]);

  const kpiCards = useMemo(() => {
    const totalScans = dashboardData?.totalScans || 0;
    const totalQRCodes = dashboardData?.totalQRCodes || 0;
    const totalCampaigns = dashboardData?.totalCampaigns || 0;
    const styledQRCodes = dashboardData?.styledQRCodes || 0;
    
    // Calculate success rate from recent scans data
    const recentScans = dashboardData?.recentScans || [];
    const successfulScans = recentScans?.filter(scan => !scan?.error && scan?.converted !== false)?.length || 0;
    const successRate = recentScans?.length > 0 ? (successfulScans / recentScans?.length * 100)?.toFixed(1) : '0.0';
    
    // Calculate growth trends from scan trends data
    const trends = dashboardData?.scanTrends || [];
    const currentPeriodScans = trends?.slice(-7)?.reduce((sum, day) => sum + (day?.scans || 0), 0);
    const previousPeriodScans = trends?.slice(-14, -7)?.reduce((sum, day) => sum + (day?.scans || 0), 0);
    
    const scanGrowth = previousPeriodScans > 0 
      ? (((currentPeriodScans - previousPeriodScans) / previousPeriodScans) * 100)?.toFixed(1)
      : '0.0';
    
    const qrGrowth = totalQRCodes > 0 ? '+5.2' : '0.0';
    const campaignGrowth = totalCampaigns > 0 ? '+2.1' : '0.0';
    
    const styledQRPercentage = totalQRCodes > 0 ? ((styledQRCodes / totalQRCodes) * 100)?.toFixed(1) : '0';
    
    return [
      {
        title: 'Total Scans',
        value: totalScans?.toLocaleString() || '0',
        change: `${scanGrowth >= 0 ? '+' : ''}${scanGrowth}%`,
        trend: parseFloat(scanGrowth) >= 0 ? 'up' : 'down',
        icon: 'scan',
        subtitle: styledQRCodes > 0 ? `${styledQRCodes} styled QR codes` : null
      },
      {
        title: 'Active QR Codes',
        value: totalQRCodes?.toLocaleString() || '0',
        change: `+${qrGrowth}%`,
        trend: 'up',
        icon: 'qr',
        subtitle: styledQRCodes > 0 ? `${styledQRPercentage}% with advanced styling` : null
      },
      {
        title: 'Campaigns',
        value: totalCampaigns?.toLocaleString() || '0',
        change: `+${campaignGrowth}%`,
        trend: 'up',
        icon: 'campaign'
      },
      {
        title: 'Success Rate',
        value: `${successRate}%`,
        change: successRate > 90 ? '+1.1%' : successRate > 80 ? '0.0%' : '-0.5%',
        trend: successRate > 90 ? 'up' : successRate > 80 ? 'neutral' : 'down',
        icon: 'success'
      }
    ];
  }, [dashboardData]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h2 className="text-lg font-semibold text-blue-900 mb-2">Preview Mode</h2>
            <p className="text-blue-700">
              Sign in to access real-time QR code analytics and manage your campaigns.
            </p>
          </div>
          
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {kpiCards?.map((card, index) => (
              <KPICard key={index} {...card} />
            ))}
          </div>

          {/* Charts and Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <ScanTrendsChart data={[]} onDataPointClick={() => {}} />
            </div>
            <div>
              <TopPerformingQRs qrCodes={[]} onQRClick={() => {}} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RealTimeScanFeed scans={[]} onTogglePause={() => {}} />
            <GeographicMiniHeatmap data={[]} onViewFullMap={() => {}} />
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNavigation />
      
      <main className="p-4 sm:p-6 lg:p-8 pb-20 sm:pb-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <GlobalControls 
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            selectedCampaign={selectedCampaign}
            onCampaignChange={setSelectedCampaign}
            selectedDateRange={dateRange}
            autoRefreshInterval={30000}
            onAutoRefreshChange={() => {}}
            isAutoRefreshEnabled={true}
            onToggleAutoRefresh={() => {}}
          />

          {/* Detailed Analytics Toggle */}
          <div className="flex items-center justify-between">
            <div></div>
            <Button
              onClick={() => setShowDetailedAnalytics(!showDetailedAnalytics)}
              variant={showDetailedAnalytics ? 'primary' : 'outline'}
              className="flex items-center space-x-2"
            >
              <Icon name="BarChart3" size={16} />
              <span>{showDetailedAnalytics ? 'Hide' : 'Show'} Detailed Analytics</span>
            </Button>
          </div>

          {userProfile?.role === 'admin' && (
            <SystemVersionManager />
          )}

          {trackingSetupStatus && trackingSetupStatus?.processed > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                <div>
                  <h4 className="text-sm font-medium text-blue-900">Analytics Setup Complete</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    {trackingSetupStatus?.message} All QR codes (including styled ones) are now trackable in the dashboard.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Detailed Analytics Panel */}
          {showDetailedAnalytics && (
            <DetailedAnalyticsPanel 
              dateRange={dateRange}
              campaignId={selectedCampaign}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {kpiCards?.map((card, index) => (
              <div key={index}>
                <KPICard {...card} loading={loading} />
                {card?.subtitle && (
                  <p className="text-xs text-gray-500 mt-1 text-center">{card?.subtitle}</p>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <ScanTrendsChart data={dashboardData?.scanTrends} loading={loading} onDataPointClick={() => {}} />
            </div>
            <div>
              <TopPerformingQRs 
                qrCodes={dashboardData?.topPerformingQRs} 
                loading={loading} 
                onQRClick={() => {}}
                showStylingInfo={true}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RealTimeScanFeed scans={dashboardData?.recentScans} loading={loading} onTogglePause={() => {}} />
            <GeographicMiniHeatmap data={dashboardData?.geographicData} loading={loading} onViewFullMap={() => {}} />
          </div>

          <div className="fixed bottom-4 right-4 z-10">
            <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 shadow-sm">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <div className="text-xs">
                  <div className="text-green-700">Real-time updates active</div>
                  {dashboardData?.styledQRCodes > 0 && (
                    <div className="text-green-600">{dashboardData?.styledQRCodes} styled QRs tracked</div>
                  )}
                  {showDetailedAnalytics && (
                    <div className="text-green-600">Detailed analytics visible</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MainAnalyticsDashboard;